<script>
import SbTableLite from "./SbTableLite.vue";
import { formattedValue, getValue, normalizeFields, searchable } from "./table";
export default {
    name: "SbTable",
    emits: ["provider-error", "refreshed", "sort-changed", "update:busy", "update:sortBy", "update:sortDesc"],
    extends: SbTableLite,
    props: {
        items: { type: [Array, Function], default: () => [] },
        sortBy: { type: String, default: null },
        sortDesc: { type: Boolean, default: false },
        filter: { default: null },
        filterIncludedFields: { type: Array, default: () => [] },
        currentPage: { type: [Number, String], default: 1 },
        perPage: { type: [Number, String], default: 0 },
    },
    data() {
        return { localSortBy: this.sortBy, localSortDesc: this.sortDesc, providerItems: [], providerBusy: false, requestId: 0, refreshScheduled: false };
    },
    computed: {
        isProvider() { return typeof this.items === "function"; },
        activeSortBy() { return this.localSortBy; },
        activeSortDesc() { return this.localSortDesc; },
        displayItems() {
            if (this.isProvider) { return this.providerItems; }
            let items = [...this.items];
            const filter = typeof this.filter === "object" ? this.filter?.text : this.filter;
            if (filter !== null && filter !== undefined && String(filter).length) {
                const needle = String(filter).toLocaleLowerCase();
                const normalizedFields = normalizeFields(this.fields, items);
                const keys = this.filterIncludedFields.length ? this.filterIncludedFields : normalizedFields.map((field) => field.key);
                items = items.filter((item) => keys.some((key) => {
                    const field = normalizedFields.find((candidate) => candidate.key === key) || { key };
                    return searchable(this.filterValue(item, field)).includes(needle);
                }));
            }
            if (this.localSortBy) {
                const field = normalizeFields(this.fields, items).find((candidate) => candidate.key === this.localSortBy) || { key: this.localSortBy };
                items.sort((left, right) => {
                    const leftValue = this.sortValue(left, field);
                    const rightValue = this.sortValue(right, field);
                    return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, { numeric: true, sensitivity: "base" }) * (this.localSortDesc ? -1 : 1);
                });
            }
            const perPage = Number(this.perPage);
            if (perPage > 0) { const start = (Math.max(1, Number(this.currentPage)) - 1) * perPage; items = items.slice(start, start + perPage); }
            return items;
        },
    },
    watch: {
        sortBy(value) { this.localSortBy = value; this.refreshProvider(); },
        sortDesc(value) { this.localSortDesc = value; this.refreshProvider(); },
        items(value, previous) { if (typeof value === "function" && value !== previous) { this.refreshProvider(); } },
        currentPage: "refreshProvider",
        perPage: "refreshProvider",
        filter: { deep: true, handler: "refreshProvider" },
    },
    mounted() { if (this.isProvider) { this.refresh(); } },
    beforeUnmount() { this.requestId += 1; },
    methods: {
        sortValue(item, field) {
            const value = getValue(item, field.key);
            if (typeof field.sortByFormatted === "function") { return field.sortByFormatted(value, field.key, item); }
            return field.sortByFormatted ? formattedValue(item, field) : value;
        },
        filterValue(item, field) {
            const value = getValue(item, field.key);
            if (typeof field.filterByFormatted === "function") { return field.filterByFormatted(value, field.key, item); }
            return field.filterByFormatted ? formattedValue(item, field) : value;
        },
        onHeaderClick(field) {
            if (!field.sortable) { return; }
            if (this.localSortBy === field.key) { this.localSortDesc = !this.localSortDesc; }
            else { this.localSortBy = field.key; this.localSortDesc = field.sortDirection === "desc"; }
            this.$emit("update:sortBy", this.localSortBy);
            this.$emit("update:sortDesc", this.localSortDesc);
            this.$emit("sort-changed", this.context());
            this.refreshProvider();
        },
        context() { return { currentPage: Number(this.currentPage) || 1, perPage: Number(this.perPage) || 0, filter: this.filter, sortBy: this.localSortBy, sortDesc: this.localSortDesc }; },
        refreshProvider() {
            if (!this.isProvider || this.refreshScheduled) { return; }
            this.refreshScheduled = true;
            this.$nextTick(() => {
                this.refreshScheduled = false;
                if (!this._isBeingDestroyed && !this._isDestroyed) { this.refresh(); }
            });
        },
        async refresh() {
            if (!this.isProvider) { return; }
            const requestId = ++this.requestId;
            this.providerBusy = true;
            this.$emit("update:busy", true);
            try {
                const items = await this.items(this.context());
                if (requestId === this.requestId) { this.providerItems = Array.isArray(items) ? items : []; }
            } catch (error) {
                if (requestId === this.requestId) { this.providerItems = []; this.$emit("provider-error", error); }
            } finally {
                if (requestId === this.requestId) { this.providerBusy = false; this.$emit("update:busy", false); this.$emit("refreshed"); }
            }
        },
    },
};
</script>
